import React from 'react';
import { AlertCircle } from 'lucide-react';
import './DetailBlocks.css';

// Piezas de la ficha de lectura dentro de DetailPanel, para que todas las
// fichas presenten sus datos igual.

interface DetailHeaderButtonProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Tooltip. Con `disabled`, explica por qué no se puede. */
  title?: string;
}

/** Botón de la cabecera del panel, junto a la X (p. ej. Editar). */
export const DetailHeaderButton = ({ icon: Icon, label, onClick, disabled, title }: DetailHeaderButtonProps) => (
  <button type="button" className="detail-header-btn" onClick={onClick} disabled={disabled} title={title}>
    <Icon size={12} />
    {label}
  </button>
);

interface DetailIdentityProps {
  /** Nombre del registro, en grande */
  name: React.ReactNode;
  /** Línea pequeña sobre el nombre (p. ej. "// REF_CATÁLOGO") */
  reference?: React.ReactNode;
  /** Línea bajo el nombre (p. ej. el autor) */
  subtitle?: React.ReactNode;
  /** Icono de fondo, decorativo */
  icon?: React.ElementType;
}

/** Bloque de cabecera de la ficha: qué registro es. */
export const DetailIdentity = ({ name, reference, subtitle, icon: Icon }: DetailIdentityProps) => (
  <div className="detail-identity">
    {Icon && (
      <div className="detail-identity-bg-icon">
        <Icon size={120} />
      </div>
    )}
    {reference && <span className="detail-identity-ref">{reference}</span>}
    <h4 className="detail-identity-name">{name}</h4>
    {subtitle && <p className="detail-identity-subtitle">{subtitle}</p>}
  </div>
);

/** Lista de campos de solo lectura. */
export const DetailFieldList = ({ children }: { children: React.ReactNode }) => (
  <div className="detail-field-list">{children}</div>
);

interface DetailFieldProps {
  label: string;
  icon?: React.ElementType;
  /** Texto o número: se pinta con el estilo estándar. Un elemento se pinta tal cual. */
  children: React.ReactNode;
}

/** Fila etiqueta / valor de la ficha. */
export const DetailField = ({ label, icon: Icon, children }: DetailFieldProps) => (
  <div className="detail-field">
    <span className="detail-field-label">
      {Icon && <Icon size={12} className="detail-field-icon" />}
      {label}
    </span>
    {typeof children === 'string' || typeof children === 'number'
      ? <span className="detail-field-value">{children}</span>
      : children}
  </div>
);

interface DetailSectionProps {
  title: React.ReactNode;
  icon?: React.ElementType;
  children: React.ReactNode;
}

/** Bloque con título dentro del panel (p. ej. "Activos fijos asociados", o una sección del formulario). */
export const DetailSection = ({ title, icon: Icon, children }: DetailSectionProps) => (
  <div className="detail-section">
    <h4 className="detail-section-title">
      {Icon && <Icon size={12} className="detail-section-icon" />}
      {title}
    </h4>
    {children}
  </div>
);

/**
 * Caja separada bajo el panel (se pasa en `cards` de DetailPanel), para
 * bloques que van aparte de los datos comunes: historial, líneas, relacionados…
 */
export const DetailCard = ({ title, icon: Icon, children }: DetailSectionProps) => (
  <div className="detail-card">
    <h4 className="detail-section-title">
      {Icon && <Icon size={12} className="detail-section-icon" />}
      {title}
    </h4>
    {children}
  </div>
);

/** Observaciones internas del registro, en ámbar. */
export const DetailNotes = ({ title = 'Observaciones', children }: { title?: string; children: React.ReactNode }) => (
  <div className="detail-notes">
    <h4 className="detail-notes-title">{title}</h4>
    <p className="detail-notes-text">{children}</p>
  </div>
);

/** Aviso destacado en rojo dentro de la ficha (p. ej. stock crítico). */
export const DetailAlert = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="detail-alert">
    <AlertCircle size={16} className="detail-alert-icon" />
    <div className="detail-alert-content">
      <h5 className="detail-alert-title">{title}</h5>
      <p className="detail-alert-desc">{children}</p>
    </div>
  </div>
);
